import { Check, Lock, Play } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { isModuleAccessible } from "@/lib/courses/isModuleAccessible";
import type { CourseModule } from "@/types/course";

interface Props {
  module: CourseModule;
  onClick: () => void;
  active?: boolean;
}

const CourseModuleRow = ({ module, onClick, active = false }: Props) => {
  const { t } = useTranslation();
  const accessible = isModuleAccessible(module);
  const thumbnail = module.thumbnailUrl;

  const stateClasses = active
    ? "border-primary/50 bg-primary/5 ring-1 ring-primary/30"
    : accessible
      ? "border-border hover:border-primary/40 hover:bg-muted/40"
      : "border-border/60 hover:border-border hover:bg-muted/30";

  return (
    <div
      className={`group flex items-center gap-3 sm:gap-4 rounded-xl border p-2.5 sm:p-3 cursor-pointer select-none transition-all ${stateClasses}`}
      onClick={onClick}
    >
      {/* Thumbnail — real frame when unlocked, branded placeholder when not */}
      <div className="relative w-24 sm:w-32 aspect-video flex-shrink-0 rounded-lg overflow-hidden bg-muted">
        {thumbnail ? (
          <img
            src={thumbnail}
            alt=""
            loading="lazy"
            className={`w-full h-full object-cover transition-transform duration-300 ${
              accessible ? "group-hover:scale-105" : ""
            }`}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-muted via-muted to-primary/10 flex items-center justify-center">
            <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground/70" />
          </div>
        )}

        {/* Overlay state badge */}
        <div
          className={`absolute inset-0 flex items-center justify-center transition-colors ${
            module.isCompleted
              ? "bg-black/45"
              : accessible
                ? "bg-black/25 group-hover:bg-black/10"
                : "bg-black/35"
          }`}
        >
          {module.isCompleted ? (
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary flex items-center justify-center shadow-lg">
              <Check className="w-4 h-4 text-primary-foreground" />
            </div>
          ) : accessible ? (
            <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/95 flex items-center justify-center shadow-lg transition-transform group-hover:scale-110">
              <Play className="w-3.5 h-3.5 text-black ml-0.5 fill-black" />
            </div>
          ) : null}
        </div>
      </div>

      {/* Title block */}
      <div className="flex-1 min-w-0 space-y-1">
        <p
          className={`text-sm font-semibold leading-snug line-clamp-2 ${
            accessible ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          {module.title}
        </p>
        {module.description && (
          <p
            className={`line-clamp-2 text-xs leading-relaxed ${
              accessible
                ? "text-muted-foreground"
                : "text-muted-foreground/70"
            }`}
          >
            {module.description}
          </p>
        )}
      </div>

      {/* Trailing badge */}
      <div className="flex-shrink-0">
        {module.isCompleted ? (
          <Badge
            variant="outline"
            className="text-[10px] sm:text-xs text-primary border-primary/30 bg-primary/10"
          >
            {t("course.completed")}
          </Badge>
        ) : module.isFree ? (
          <Badge
            variant="outline"
            className="text-[10px] sm:text-xs text-primary border-primary/30 bg-primary/10"
          >
            {t("course.freeLabel")}
          </Badge>
        ) : !accessible ? (
          <Lock className="w-4 h-4 text-muted-foreground/60" />
        ) : null}
      </div>
    </div>
  );
};

export default CourseModuleRow;
